// src/stores/v2/devices.ts
import { defineStore } from 'pinia'
import { ref, computed, readonly } from 'vue'
import { useAgentStateService } from '@/services/agent/AgentStateService'
import { useAuthStore } from '@/stores/authStore'
import {
  readScaleAndWait,
  captureCameraAndWait,
  scanIdAndWait,
} from '@/services/deviceCommandService'
import type {
  Device,
  Scale,
  Camera,
  Scanner,
  ScaleReading,
  CombinedCaptureResult,
} from '@/types/Agent'

export const useDevicesStore = defineStore('devices.v2', () => {
  // State
  const loading = ref(false)
  const agents = ref([] as any[])
  const defaultScaleId = ref<string | null>(null)
  const defaultCameraId = ref<string | null>(null)
  const defaultScannerId = ref<string | null>(null)

  // Services
  const agentStateService = useAgentStateService()
  const authStore = useAuthStore()

  // Initialize agent presence subscription
  let unsubscribe: (() => void) | null = null

  function initialize() {
    if (unsubscribe) return // Already initialized

    // Load defaults from localStorage
    loadDefaults()

    // Subscribe to agent presence
    unsubscribe = agentStateService.subscribeToAgentPresence((agentStates) => {
      agents.value = agentStates
    })
  }

  function cleanup() {
    if (unsubscribe) {
      unsubscribe()
      unsubscribe = null
    }
  }

  function loadDefaults() {
    try {
      const stored = localStorage.getItem('scrapboss-device-defaults')
      if (stored) {
        const parsed = JSON.parse(stored)
        defaultScaleId.value = parsed.defaultScaleId || null
        defaultCameraId.value = parsed.defaultCameraId || null
        defaultScannerId.value = parsed.defaultScannerId || null
      }
    } catch (error) {
      console.error('Failed to load device defaults:', error)
    }
  }

  function saveDefaults() {
    try {
      localStorage.setItem(
        'scrapboss-device-defaults',
        JSON.stringify({
          defaultScaleId: defaultScaleId.value,
          defaultCameraId: defaultCameraId.value,
          defaultScannerId: defaultScannerId.value,
        }),
      )
    } catch (error) {
      console.error('Failed to save device defaults:', error)
    }
  }

  // Computed getters
  const scales = computed(() => {
    const scaleList: Scale[] = []
    agents.value.forEach((agent) => {
      const list = (agent.devices?.scales as any[]) || []
      list.forEach((device: any) => {
        scaleList.push({
          ...device,
          id: device.id,
          type: 'scale',
          name: device.name,
          location: device.location,
          driver: device.driver,
          ops: device.ops || [],
          assignedCameraId: device.assignedCameraId,
        })
      })
    })
    return scaleList
  })

  const cameras = computed(() => {
    const cameraList: Camera[] = []
    agents.value.forEach((agent) => {
      const list = (agent.devices?.cameras as any[]) || []
      list.forEach((device: any) => {
        cameraList.push({
          ...device,
          id: device.id,
          type: 'camera',
          name: device.name,
          location: device.location,
          ops: device.ops || [],
          modes: device.modes || [],
        })
      })
    })
    return cameraList
  })

  const scanners = computed(() => {
    const scannerList: Scanner[] = []
    agents.value.forEach((agent) => {
      const list = (agent.devices?.scanners as any[]) || []
      list.forEach((device: any) => {
        scannerList.push({
          ...device,
          id: device.id,
          type: 'scanner',
          name: device.name,
          location: device.location,
          driver: device.driver,
          ops: device.ops || [],
        })
      })
    })
    return scannerList
  })

  const defaultScale = computed(() => {
    if (defaultScaleId.value) {
      return scales.value.find((s) => s.id === defaultScaleId.value) || null
    }
    return scales.value.length === 1 ? scales.value[0] : null
  })

  const defaultCamera = computed(() => {
    if (defaultCameraId.value) {
      return cameras.value.find((c) => c.id === defaultCameraId.value) || null
    }
    return cameras.value.length === 1 ? cameras.value[0] : null
  })

  const defaultScanner = computed(() => {
    if (defaultScannerId.value) {
      return scanners.value.find((s) => s.id === defaultScannerId.value) || null
    }
    return scanners.value.length === 1 ? scanners.value[0] : null
  })

  const getDeviceById = (id: string): Device | null => {
    for (const agent of agents.value) {
      const d =
        ((agent.devices?.scales as any[]) || []).find((x) => x.id === id) ||
        ((agent.devices?.cameras as any[]) || []).find((x) => x.id === id) ||
        ((agent.devices?.scanners as any[]) || []).find((x) => x.id === id)
      if (d) return d as Device
    }
    return null
  }

  const getScaleWithCamera = (scaleId: string): { scale: Scale; camera: Camera | null } | null => {
    const scale = scales.value.find((s) => s.id === scaleId)
    if (!scale) return null

    let camera: Camera | null = null
    if (scale.assignedCameraId) {
      camera = cameras.value.find((c) => c.id === scale.assignedCameraId) || null
    }

    return { scale, camera }
  }

  const getAgentForDevice = (deviceId: string): any | null => {
    for (const agent of agents.value) {
      const found =
        ((agent.devices?.scales as any[]) || []).some((x) => x.id === deviceId) ||
        ((agent.devices?.cameras as any[]) || []).some((x) => x.id === deviceId) ||
        ((agent.devices?.scanners as any[]) || []).some((x) => x.id === deviceId)
      if (found) return agent
    }
    return null
  }

  // Actions
  function setDefaultScale(scaleId: string | null) {
    defaultScaleId.value = scaleId
    saveDefaults()
  }

  function setDefaultCamera(cameraId: string | null) {
    defaultCameraId.value = cameraId
    saveDefaults()
  }

  function setDefaultScanner(scannerId: string | null) {
    defaultScannerId.value = scannerId
    saveDefaults()
  }

  async function readScale(scaleId?: string): Promise<ScaleReading> {
    const targetScaleId = scaleId || defaultScale.value?.id
    if (!targetScaleId) {
      throw new Error('No scale selected and no default scale available')
    }

    const agent = getAgentForDevice(targetScaleId)
    if (!agent) {
      throw new Error(`Agent not found for scale ${targetScaleId}`)
    }

    return await readScaleAndWait(authStore.session, agent.id, targetScaleId)
  }

  async function captureScaleWithImage(scaleId?: string): Promise<CombinedCaptureResult> {
    const targetScaleId = scaleId || defaultScale.value?.id
    if (!targetScaleId) {
      throw new Error('No scale selected and no default scale available')
    }

    const scaleWithCamera = getScaleWithCamera(targetScaleId)
    if (!scaleWithCamera) {
      throw new Error(`Scale ${targetScaleId} not found`)
    }

    const agent = getAgentForDevice(targetScaleId)
    if (!agent) {
      throw new Error(`Agent not found for scale ${targetScaleId}`)
    }

    // Read weight
    const weight = await readScaleAndWait(authStore.session, agent.id, targetScaleId)

    // Capture image if camera is assigned
    let image: { url: string; storagePath: string } | null = null
    if (scaleWithCamera.camera) {
      try {
        const imageResult = await captureCameraAndWait(
          authStore.session,
          agent.id,
          scaleWithCamera.camera.id,
        )
        image = imageResult
      } catch (error) {
        console.warn('Failed to capture image:', error)
        // Continue without image
      }
    }

    return { weight, image }
  }

  async function captureCamera(cameraId?: string): Promise<{ url: string; storagePath: string }> {
    const targetCameraId = cameraId || defaultCamera.value?.id
    if (!targetCameraId) {
      throw new Error('No camera selected and no default camera available')
    }

    const agent = getAgentForDevice(targetCameraId)
    if (!agent) {
      throw new Error(`Agent not found for camera ${targetCameraId}`)
    }

    return await captureCameraAndWait(authStore.session, agent.id, targetCameraId)
  }

  async function scanId(scannerId?: string): Promise<{ text: string }> {
    const targetScannerId = scannerId || defaultScanner.value?.id
    if (!targetScannerId) {
      throw new Error('No scanner selected and no default scanner available')
    }

    const agent = getAgentForDevice(targetScannerId)
    if (!agent) {
      throw new Error(`Agent not found for scanner ${targetScannerId}`)
    }

    return await scanIdAndWait(authStore.session, agent.id, { scannerId: targetScannerId })
  }

  // Initialize on store creation
  initialize()

  return {
    // State
    loading: readonly(loading),
    agents: readonly(agents),
    defaultScaleId: readonly(defaultScaleId),
    defaultCameraId: readonly(defaultCameraId),
    defaultScannerId: readonly(defaultScannerId),

    // Computed
    scales,
    cameras,
    scanners,
    defaultScale,
    defaultCamera,
    defaultScanner,
    getDeviceById,
    getScaleWithCamera,
    getAgentForDevice,

    // Actions
    setDefaultScale,
    setDefaultCamera,
    setDefaultScanner,
    readScale,
    captureScaleWithImage,
    captureCamera,
    scanId,
    cleanup,
  }
})
